import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import Backend from "i18next-http-backend";
import LanguageDetector from "i18next-browser-languagedetector";
import { getDomainConfig } from "./domains";

// 🆕 Визначення мови за портом для розробки
const getLanguageByPort = (): string | null => {
  if (!import.meta.env.DEV) return null;

  const port = window.location.port;
  const portLanguageMap: Record<string, string> = {
    "5173": "de", // Німецька (за замовчуванням)
    "5174": "ua", // Українська
    "5175": "pl", // Польська
    "5176": "cz", // Чеська
  };

  return portLanguageMap[port] || null;
};

const domainConfig = getDomainConfig();
const portLanguage = getLanguageByPort();

// 🆕 Якщо розробка і є мова для порту - використовуємо її
const initialLanguage = portLanguage || domainConfig.language;

// ✅ Використовуємо асинхронну ініціалізацію
export const initI18n = async () => {
  await i18n
    .use(Backend)
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      lng: initialLanguage,
      fallbackLng: "de",
      supportedLngs: ["de", "pl", "ua", "cz"],

      // 🆕 Додаємо namespace для різних типів перекладів
      ns: ["common", "pages", "images"],
      defaultNS: "common",

      debug: import.meta.env.DEV,

      interpolation: {
        escapeValue: false,
        // Підтримка HTML в перекладах
        skipOnVariables: false,
      },

      backend: {
        // 🆕 Оновлений шлях з підтримкою namespaces
        // {{lng}} - мова (de, pl, ua, cz)
        // {{ns}} - namespace (common, pages, images)
        loadPath: "/locales/{{lng}}/{{ns}}.json",

        // Додаткові налаштування для backend
        requestOptions: {
          mode: "cors",
          credentials: "same-origin",
          cache: "default",
        },
      },

      detection: {
        // 🆕 В dev режимі ігноруємо detection якщо є portLanguage
        order: portLanguage
          ? [] // Не визначаємо автоматично, використовуємо lng
          : ["path", "cookie", "localStorage", "navigator"],
        caches: ["cookie", "localStorage"],
        cookieMinutes: 10080, // 7 днів
        lookupCookie: "i18next",
        lookupLocalStorage: "i18nextLng",
      },

      react: {
        useSuspense: true, // ✅ ЗМІНЕНО: false для react-i18next 15 з React 18
        // 🆕 Додаємо підтримку Trans компонента з HTML
        transSupportBasicHtmlNodes: true,
        transKeepBasicHtmlNodesFor: ["br", "strong", "i", "p", "span"],
      },

      // 🆕 Логування відсутніх перекладів (тільки в dev)
      saveMissing: import.meta.env.DEV,
      missingKeyHandler: (lng, ns, key) => {
        if (import.meta.env.DEV) {
          console.warn(`🔴 Missing translation: [${lng}][${ns}] ${key}`);
        }
      },

      // 🆕 Обробка помилок завантаження
      load: "languageOnly", // завантажуємо тільки 'de', не 'de-DE'
    });

  return i18n;
};

// Експортуємо для використання в інших місцях
export default i18n;

// 🆕 Типи для TypeScript
export type SupportedLanguage = "de" | "pl" | "ua" | "cz";
export type Namespace = "common" | "pages" | "images";
